import React from 'react';
import { Bot, Loader2 } from 'lucide-react';
import { StreamingState, StreamingToolCall } from '@/hooks/useStreamingChat';
import LiveToolCallStream from './LiveToolCallStream';

interface StreamingMessageProps {
  streamingState: StreamingState;
}

const StreamingMessage: React.FC<StreamingMessageProps> = ({ streamingState }) => {

  // Show streaming message if:
  // 1. Currently streaming (any phase)
  // 2. Has tool calls to display (even if not actively streaming)
  // 3. Has a current response to show
  // 4. Has an error to display
  if (!streamingState.isStreaming && 
      streamingState.toolCalls.length === 0 && 
      !streamingState.currentResponse && 
      !streamingState.error) {
    return null;
  }

  const renderStatusIndicator = () => {
    if (streamingState.error) {
      return (
        <div className="flex items-center gap-2 text-red-500">
          <div className="w-2 h-2 bg-red-500 rounded-full"></div>
          <span className="text-xs">Error: {streamingState.error}</span>
        </div>
      );
    }

    // Remove thinking indicator as requested - we don't want the color mismatch
    // if (streamingState.isThinking) {
    //   return (
    //     <div className="flex items-center gap-2 text-muted-foreground">
    //       <div className="flex space-x-1">
    //         <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce"></div>
    //         <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
    //         <div className="w-1.5 h-1.5 bg-muted-foreground rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
    //       </div>
    //       <span className="text-xs">Thinking...</span>
    //     </div>
    //   );
    // }

    if (streamingState.isStreaming) {
      // Show different status based on activity
      const hasRunningTools = streamingState.toolCalls.some(tc => tc.status === 'running');
      const statusText = streamingState.agentStatus || (hasRunningTools ? 'Processing...' : 'Thinking...');
      
      return (
        <div className="flex items-center gap-2 text-blue-500">
          <Loader2 className="w-3 h-3 animate-spin" />
          <span className="text-xs">{statusText}</span>
        </div>
      );
    }

    return null;
  };

  return (
    <div className="flex items-start space-x-3 mb-6">
      {/* Bot Avatar - Match the regular message avatar */}
      <div className="w-7 h-7 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-xs font-medium flex-shrink-0 text-green-700 dark:text-green-400">
        AI
      </div>

      <div className="flex-1 min-w-0">
        {/* Tool calls display */}
        {streamingState.toolCalls.length > 0 && (
          <div className="mb-3">
            <LiveToolCallStream toolCalls={streamingState.toolCalls} />
          </div>
        )}

        {/* Status indicator - show if streaming or has tool calls to display */}
        {(streamingState.isStreaming || streamingState.toolCalls.length > 0) && renderStatusIndicator()}

        {/* Response content - show any accumulated response */}
        {streamingState.currentResponse && streamingState.currentResponse.trim().length > 0 && (
          <div className={streamingState.toolCalls.length > 0 ? "mt-2" : "mt-1"}>
            <div className="prose prose-sm max-w-none dark:prose-invert">
              {/* Render response with basic markdown-like formatting */}
              {streamingState.currentResponse.split('\n').map((line, index) => (
                <div key={index} className={line.trim() === '' ? 'h-2' : ''}>
                  {line.trim() === '' ? '' : line}
                </div>
              ))}
            </div>
            
            {/* Show typing indicator if still streaming and response is being generated */}
            {streamingState.isStreaming && (
              <div className="inline-flex items-center mt-1">
                <div className="w-1 h-4 bg-primary animate-pulse rounded-sm"></div>
              </div>
            )}
          </div>
        )}

        {/* Completion indicator */}
        {!streamingState.isStreaming && streamingState.currentResponse && !streamingState.error && (
          <div className="flex items-center gap-1 mt-2 text-green-600 text-xs">
            <div className="w-2 h-2 bg-green-600 rounded-full"></div>
            <span>Complete</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default StreamingMessage;